import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  QrCode, MessageCircle, Stamp, Gift, ArrowRight, ArrowDown,
  Smartphone, Store, CheckCircle2, Sparkles, Users, TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';

const HowItWorksPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const steps = [
    {
      number: "01",
      icon: <Store className="w-8 h-8" />,
      title: "Configura tu campaña",
      description: "Crea tu programa de fidelización en minutos. Define cuántos sellos necesita el cliente para obtener su recompensa y qué premio recibirá.",
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50",
      details: ["Define el número de sellos", "Elige la recompensa", "Genera tu código QR único"]
    },
    {
      number: "02",
      icon: <QrCode className="w-8 h-8" />,
      title: "El cliente escanea el QR",
      description: "Coloca el código QR en tu punto de venta. Cuando el cliente realiza una compra, simplemente escanea el código con su móvil.",
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50",
      details: ["Sin apps que descargar", "Funciona con cualquier móvil", "Escaneo instantáneo"]
    },
    {
      number: "03",
      icon: <MessageCircle className="w-8 h-8" />,
      title: "Envía por WhatsApp",
      description: "Se abre WhatsApp automáticamente con un mensaje preparado. El cliente solo tiene que pulsar enviar para registrar su visita.",
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-50",
      details: ["WhatsApp se abre automáticamente", "Mensaje pre-configurado", "Un solo clic para enviar"]
    },
    {
      number: "04",
      icon: <Stamp className="w-8 h-8" />,
      title: "Sello registrado",
      description: "El sistema valida la visita y añade un sello a la tarjeta digital del cliente. Todo queda registrado de forma segura.",
      color: "from-amber-500 to-amber-600",
      bgColor: "bg-amber-50",
      details: ["Validación automática", "Registro seguro", "Historial completo"]
    },
    {
      number: "05",
      icon: <Gift className="w-8 h-8" />,
      title: "¡Premio desbloqueado!",
      description: "Al completar todos los sellos, el cliente recibe automáticamente un voucher digital con su recompensa listo para canjear.",
      color: "from-rose-500 to-rose-600",
      bgColor: "bg-rose-50",
      details: ["Voucher digital único", "Notificación automática", "Canje fácil en tienda"]
    }
  ];

  const benefits = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Más clientes recurrentes",
      description: "Aumenta hasta un 30% las visitas de tus clientes habituales"
    },
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: "Sin fricción",
      description: "El cliente no necesita descargar ninguna app ni registrarse"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Datos en tiempo real",
      description: "Visualiza la actividad de tu programa desde el panel de control"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 sm:h-24">
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_796fbc31-c145-4a59-88df-6b2e6dbd2615/artifacts/n7zu75fp_file_00000000fb6c7243ac605c38dbc5a990%20%281%29.png" 
                alt="Stampply Rewards" 
                className="h-auto w-36 sm:w-44 lg:w-52"
              />
            </Link>
            
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="font-semibold text-slate-700 hover:text-slate-900">
                  Iniciar Sesión
                </Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary hidden sm:flex">
                  Prueba Gratis
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-28 pb-16 sm:pb-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Así de fácil
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight mb-6">
              Cómo funciona{' '}
              <span className="text-primary">Stampply</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto">
              Un proceso simple y sin fricciones para fidelizar a tus clientes usando el canal que ya conocen: WhatsApp.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            {/* Connection Line - Desktop */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-200 via-green-200 to-rose-200 -translate-x-1/2" />
            
            {steps.map((step, index) => (
              <motion.div
                key={index}
                className={`relative flex flex-col lg:flex-row items-center gap-8 lg:gap-16 mb-16 sm:mb-24 last:mb-0 ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                {/* Step Number Circle - Desktop */}
                <div className="hidden lg:flex absolute left-1/2 -translate-x-1/2 w-16 h-16 bg-white border-4 border-slate-200 rounded-full items-center justify-center z-10">
                  <span className="text-2xl font-bold text-slate-400">{step.number}</span>
                </div>

                {/* Content */}
                <div className={`flex-1 ${index % 2 === 1 ? 'lg:text-right' : ''}`}>
                  <div className={`inline-block ${index % 2 === 1 ? 'lg:float-right' : ''}`}>
                    {/* Mobile Step Number */}
                    <div className="lg:hidden flex items-center gap-3 mb-4">
                      <div className={`w-12 h-12 bg-gradient-to-br ${step.color} rounded-xl flex items-center justify-center text-white shadow-lg`}>
                        {step.icon}
                      </div>
                      <span className="text-4xl font-extrabold text-slate-200">{step.number}</span>
                    </div>

                    <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                      {step.title}
                    </h3>
                    <p className="text-lg text-slate-600 mb-6 max-w-md">
                      {step.description}
                    </p>
                    <ul className={`space-y-2 ${index % 2 === 1 ? 'lg:text-right' : ''}`}>
                      {step.details.map((detail, i) => (
                        <li key={i} className={`flex items-center gap-2 text-slate-500 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                          <CheckCircle2 className="w-5 h-5 text-secondary flex-shrink-0" />
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Visual Card */}
                <div className="flex-1 w-full max-w-md lg:max-w-none">
                  <div className={`${step.bgColor} rounded-3xl p-8 sm:p-12 relative overflow-hidden`}>
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-30">
                      <div className="absolute top-4 right-4 w-32 h-32 bg-white rounded-full blur-3xl" />
                      <div className="absolute bottom-4 left-4 w-24 h-24 bg-white rounded-full blur-2xl" />
                    </div>
                    
                    {/* Icon */}
                    <div className="relative">
                      <div className={`w-24 h-24 sm:w-32 sm:h-32 mx-auto bg-gradient-to-br ${step.color} rounded-3xl flex items-center justify-center text-white shadow-2xl transform hover:scale-105 transition-transform`}>
                        <div className="scale-150 sm:scale-[2]">
                          {step.icon}
                        </div>
                      </div>
                      
                      {/* Step indicator */}
                      <div className="hidden lg:block absolute -top-4 -right-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center">
                        <span className="text-xl font-bold bg-gradient-to-br from-slate-700 to-slate-900 bg-clip-text text-transparent">
                          {step.number}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Arrow Down - Mobile */}
                {index < steps.length - 1 && (
                  <div className="lg:hidden flex justify-center w-full -mb-8">
                    <ArrowDown className="w-8 h-8 text-slate-300 animate-bounce" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-12 sm:mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              ¿Por qué elegir Stampply?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Ventajas que marcan la diferencia para tu negocio
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow border border-slate-100"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center text-primary mb-5">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{benefit.title}</h3>
                <p className="text-slate-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 sm:py-28 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              ¿Listo para empezar?
            </h2>
            <p className="text-lg sm:text-xl text-white/80 mb-10 max-w-2xl mx-auto">
              Configura tu programa de fidelización en menos de 5 minutos y empieza a ver resultados desde el primer día.
            </p>
            <Link to="/register">
              <Button className="bg-white text-primary hover:bg-white/90 text-lg px-10 py-6 font-bold shadow-xl shadow-black/20 hover:shadow-2xl transition-all">
                Pruébalo Gratis <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <p className="mt-6 text-white/60 text-sm">
              Sin tarjetas físicas · Configuración en minutos · Cancela cuando quieras
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 sm:py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-10">
            {/* Logo */}
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_796fbc31-c145-4a59-88df-6b2e6dbd2615/artifacts/tgu072lj_LOGO%20STAMPPLY%202.png" 
                alt="Stampply Rewards" 
                className="h-32 sm:h-40 md:h-48 w-auto"
              />
            </Link>
            
            {/* Links */}
            <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10 text-slate-400">
              <Link to="/terminos" className="hover:text-white transition-colors font-medium">Términos</Link>
              <Link to="/privacidad" className="hover:text-white transition-colors font-medium">Privacidad</Link>
              <Link to="/contacto" className="hover:text-white transition-colors font-medium">Contacto</Link>
            </div>
            
            {/* Copyright */}
            <p className="text-slate-600 text-sm">
              © 2025 Stampply Rewards. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HowItWorksPage;
